// React Query hooks for the WB hauliers admin surface (Phase C3 Group 1).
//
// Pairs with /api/v1/wb/hauliers/* — list (paginated + searchable) /
// detail / create / patch / delete. `enc_id` is the encrypted scalar id
// (snake_case throughout the WB module). DTOs mirror
// server/src/modules/wb/hauliers/types.ts exactly.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types (mirror WbHaulierDto on the server) -----------------

export type LicenceExpiryStatus =
  | 'not_set'
  | 'expired'
  | 'expiring_soon'
  | 'valid';

export interface WbHaulier {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
  readonly contact_name: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly address: string | null;
  readonly carrier_licence_number: string | null;
  readonly carrier_licence_expiry: string | null;
  readonly licence_expiry_status: LicenceExpiryStatus;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface Pagination {
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
  readonly total_pages: number;
}

export interface CreateHaulierInput {
  name: string;
  code: string;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  carrier_licence_number?: string | null;
  carrier_licence_expiry?: string | null;
  is_active?: boolean;
}

export type UpdateHaulierInput = Partial<CreateHaulierInput>;

export interface ListHauliersParams {
  page?: number;
  per_page?: number;
  name?: string;
  is_active?: boolean;
}

interface ListHauliersResponse {
  hauliers: WbHaulier[];
  pagination: Pagination;
}

// -- Helpers ----------------------------------------------------------------

function buildQueryString(params: ListHauliersParams): string {
  const sp = new URLSearchParams();
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.per_page !== undefined) sp.set('per_page', String(params.per_page));
  if (params.name !== undefined && params.name !== '') sp.set('name', params.name);
  if (params.is_active !== undefined) sp.set('is_active', String(params.is_active));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// -- Queries ----------------------------------------------------------------

export function useWbHauliers(params: ListHauliersParams = {}) {
  return useQuery<ListHauliersResponse>({
    queryKey: queryKeys.wb.hauliers(params as Record<string, unknown>),
    queryFn: async () => {
      const qs = buildQueryString(params);
      const res = await apiClient.get<ListHauliersResponse>(
        `${endpoints.wb.hauliers}${qs}`,
      );
      return res.data;
    },
    staleTime: 15 * 60 * 1000, // Server caches for 30min; 15min client staleTime is safe
  });
}

export function useWbHaulier(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<{ haulier: WbHaulier }>({
    queryKey: queryKeys.wb.haulier(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbHaulier called without enc_id');
      const res = await apiClient.get<{ haulier: WbHaulier }>(
        endpoints.wb.haulier(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 15 * 60 * 1000, // Server caches for 30min; 15min client staleTime is safe
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreateHaulier() {
  const qc = useQueryClient();
  return useMutation<{ haulier: WbHaulier }, Error, CreateHaulierInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ haulier: WbHaulier }, CreateHaulierInput>(
        endpoints.wb.hauliers,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.hauliers() });
    },
  });
}

export function useUpdateHaulier(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ haulier: WbHaulier }, Error, UpdateHaulierInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<{ haulier: WbHaulier }, UpdateHaulierInput>(
        endpoints.wb.haulier(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.hauliers() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.haulier(encId) });
    },
  });
}

export function useDeleteHaulier(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.haulier(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.hauliers() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.haulier(encId) });
    },
  });
}
